import React, { useEffect, useState } from 'react';

interface Video {
  video_id: string;
  title: string;
  description: string;
  thumbnail_url: string;
  video_url: string;
}

interface VideoInfoPair {
  video1: Video;
  video2: Video;
}

const VideoHighlight: React.FC = () => {
  const [videos, setVideos] = useState<VideoInfoPair | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // YouTube playlist ID - keeping this for reference
  // const playlistId = 'PLfoGiILhKUfF1sJKgBsFvmdKYNMa7fpW1';
  
  useEffect(() => {
    const fetchLatestVideos = async () => {
      try {
        const response = await fetch('https://youtube-random.bartoszbudzynski.pl/latest-videos/');
        
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        
        const data: VideoInfoPair = await response.json();
        setVideos(data);
        setLoading(false);
      } catch (error) {
        setError(error instanceof Error ? error.message : 'An error occurred');
        setLoading(false);
      }
    };

    fetchLatestVideos();
  }, []);

  if (error) return <div className="text-blue-500 p-4">Error: {error}</div>;

  return (
    <section className="py-16 px-4 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        {loading ? (
          <div className="animate-pulse text-lg text-center">Loading videos...</div>
        ) : (
          <>
            <div className="text-center mb-8">
              <h3 className="text-3xl font-bold text-[#00529B] mb-4">
                Featured Conference Videos
              </h3>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Check out these highlights to get a taste of what to expect!
              </p>
            </div>
            
            {videos && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Video 1 */}
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                  <div className="aspect-w-16 aspect-h-9">
                    <iframe 
                      src={`https://www.youtube.com/embed/${videos.video1.video_id}`}
                      title={videos.video1.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full"
                    ></iframe>
                  </div>
                  <div className="p-6">
                    <h4 className="text-xl font-semibold text-[#00529B]">{videos.video1.title}</h4>
                    {videos.video1.description && (
                      <p className="mt-2 text-gray-600 line-clamp-2">{videos.video1.description}</p>
                    )}
                    <div className="mt-4 flex justify-between items-center">
                      <a 
                        href={videos.video1.video_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-[#00529B] hover:underline"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                          <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                        </svg>
                        Watch on YouTube
                      </a>
                    </div>
                  </div>
                </div>
                
                {/* Video 2 */}
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                  <div className="aspect-w-16 aspect-h-9">
                    <iframe 
                      src={`https://www.youtube.com/embed/${videos.video2.video_id}`}
                      title={videos.video2.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full"
                    ></iframe>
                  </div>
                  <div className="p-6">
                    <h4 className="text-xl font-semibold text-[#00529B]">{videos.video2.title}</h4>
                    {videos.video2.description && (
                      <p className="mt-2 text-gray-600 line-clamp-2">{videos.video2.description}</p>
                    )}
                    <div className="mt-4 flex justify-between items-center">
                      <a 
                        href={videos.video2.video_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-[#00529B] hover:underline"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                          <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                        </svg>
                        Watch on YouTube
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div className="text-center mt-8">
              <a 
                href="https://www.youtube.com/playlist?list=PLfoGiILhKUfH8LlfiM-4TMl5busWmP46l"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center bg-[#00529B] hover:bg-[#003D74] text-white px-6 py-3 rounded-full transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                </svg>
                View All Videos
              </a>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default VideoHighlight;
